import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Modal, ModalHeader, ModalBody, ModalFooter} from 'reactstrap';
import {toast} from 'react-toastify';
import {ChannelAccountSelect} from '../../selects/ChannelAccountSelect';
import {ChannelLogo} from '../../channels/ChannelLogo';
import {CHANNEL_INFO_BY_CHANNEL} from '../../channels/channels';
import {APIClient} from '../../../utils/APIClient';
import {API_URL} from '../../../settings/local_settings';
import {isOk} from '../../../utils/apiResponse';
import {fetchCandidates} from '../../onboarding/api';
import {Spinner} from '../../Spinner';
import {candidatesByAccountId, channelHasActivity, hintKeysOf, orderAccounts} from './linkAccountOptions';
import {MissingAccountHelp} from './MissingAccountHelp';
import {accountsMatching, offersMissingAccountHelp} from './missingAccountModel';
import {fetchEmptyAccountsReason, LinkAccountEmptyState} from './LinkAccountEmptyState';
import {
    isPlanLimitError, publishAdAccountQuota, quotaFromLinkError, quotaIsFull, refetchAdAccountQuota, useAdAccountQuota,
} from '../../../utils/adAccountQuota';
import {AccountQuotaNotice} from './AccountQuotaNotice';
import {planLimitText} from './accountQuota';
import {
    fetchUnreachableLinked, findUnreachable, reconnectDataOfLinked, recordUnreachableShown, UnreachableLinkedNotice,
} from './unreachableLinked';
import {isPaidReconnectChannel} from '../../agency/connections/reconnectAccountText';
import {ReconnectAdAccountModal} from '../../agency/connections/ReconnectAdAccountModal';
import './QuickConnectAccountModal.scss';

const RAW = {escapeValue: false};
// La pantalla en los eventos del cupo (`account_quota_<acción>_link_modal`).
const QUOTA_SURFACE = 'link_modal';

/**
 * «Vincular una cuenta de {canal}» — sumarle a un cliente una cuenta que la agencia YA tiene en
 * Dashbo. Lo usan el Panel 2.1 y la ficha clásica.
 *
 * Distingue dos cosas que la gente confunde (6zUFUONB, jaM5imSj):
 * - **Vincular una cuenta existente** es lo que hace este modal, y es el camino principal.
 * - **Conectar un acceso nuevo** a la plataforma (otro login de Google, la primera conexión de
 *   Meta) es otra operación, de la agencia, que vive en Conexiones. Acá aparece sólo como salida
 *   secundaria rotulada como tal, o como único camino cuando la agencia no tiene ninguna cuenta del
 *   canal.
 *
 * Las cuentas se ordenan por actividad (`linkAccountOptions`) y las que el cliente ya tiene no se
 * ofrecen. Si la actividad no llega, el modal anda igual, sin pistas.
 *
 * En los canales de pauta, cuando la cuenta no está, «¿No encuentras la cuenta?» (`MissingAccountHelp`,
 * cc#209) dice de quién es cada conexión, por qué falta y cómo resolverlo; se abre solo cuando la
 * búsqueda no encuentra nada. Reemplaza al pie que decía «Es de un acceso que tu agencia todavía no
 * conectó», que era falso en el caso más común: la conexión existe y su usuario no tiene la cuenta.
 *
 * Sin ninguna cuenta del canal, el vacío dice por qué según las conexiones de la agencia (UD-9): nunca
 * se conectó, se cortó, está trayendo sus cuentas o su usuario no llega a ninguna (`LinkAccountEmptyState`).
 *
 * Con el cupo de cuentas del plan lleno, lo dice antes de que se elija una cuenta, con las dos salidas
 * (`AccountQuotaNotice`, UD-15). El cupo se lee fresco al abrir y, mientras no se sabe, el modal anda
 * como siempre: si el alta igual rechaza por el tope, el rechazo trae los números y el aviso aparece.
 *
 * Si lo buscado es una cuenta que YA está vinculada a este cliente y perdió el acceso, el vacío del
 * combo lo dice y ofrece «Reconectarla» (M6, UD-21; `unreachableLinked`), en vez de abrir la ayuda.
 */
export function QuickConnectAccountModal({isOpen, toggle, channel, clientId, onSuccess, linkedAccountIds}) {
    const {t, i18n} = useTranslation();
    const [accounts, setAccounts] = useState(null);
    // Las cuentas de este cliente y canal que perdieron el acceso (M6): las reconoce la búsqueda.
    const [unreachable, setUnreachable] = useState([]);
    const [reconnectRow, setReconnectRow] = useState(null);
    const unreachableSeen = useRef(new Set());
    const [candidates, setCandidates] = useState({});
    const [loadFailed, setLoadFailed] = useState(false);
    // Por qué no hay cuentas, cuando no hay (`linkAccountEmpty`); `null` mientras haya.
    const [emptyReason, setEmptyReason] = useState(null);
    const [saving, setSaving] = useState(false);
    const [query, setQuery] = useState('');
    const [helpOpen, setHelpOpen] = useState(false);
    const quota = useAdAccountQuota({enabled: Boolean(isOpen && channel)});
    // Un pedido viejo que vuelve tarde (se cerró el modal, se abrió otro canal) no pisa al nuevo.
    const requestSeq = useRef(0);

    const load = useCallback(() => {
        if (!channel) return;
        const seq = ++requestSeq.current;
        setLoadFailed(false);
        // Acompañamiento: si falla, la búsqueda dice lo de siempre.
        if (isPaidReconnectChannel(channel)) {
            fetchUnreachableLinked(clientId, channel).then((rows) => {
                if (seq === requestSeq.current) setUnreachable(rows);
            });
        }
        const accountsRequest = new APIClient(`${API_URL}get-accounts/${channel}`).call('GET');
        // La actividad es acompañamiento: si falla, se ofrecen las cuentas sin pistas.
        const activityRequest = channelHasActivity(channel)
            ? fetchCandidates([channel]).catch(() => null)
            : Promise.resolve(null);

        Promise.all([accountsRequest, activityRequest]).then(([accountsResponse, activityResponse]) => {
            if (seq !== requestSeq.current) return;
            if (!isOk(accountsResponse)) {
                setAccounts(null);
                setLoadFailed(true);
                return;
            }
            const list = accountsResponse.data || [];
            const show = (reason) => {
                if (seq !== requestSeq.current) return;
                setEmptyReason(reason);
                setAccounts(list);
                setCandidates(candidatesByAccountId(activityResponse));
            };
            // Sin cuentas, qué decir depende de las conexiones de la agencia: se piden recién acá, porque
            // casi siempre hay cuentas. Una planilla se crea en el cliente y tiene su propio vacío.
            const info = CHANNEL_INFO_BY_CHANNEL[channel];
            if (list.length > 0 || (info && info.CreateAccount)) {
                show(null);
                return;
            }
            fetchEmptyAccountsReason(channel).then(show);
        });
    }, [channel, clientId]);

    useEffect(() => {
        if (isOpen && channel) {
            load();
            return undefined;
        }
        requestSeq.current += 1;
        setAccounts(null);
        setCandidates({});
        setLoadFailed(false);
        setEmptyReason(null);
        setSaving(false);
        setQuery('');
        setHelpOpen(false);
        setUnreachable([]);
        setReconnectRow(null);
        unreachableSeen.current = new Set();
        return undefined;
    }, [isOpen, channel, load]);

    // Quien fue a Conexiones (otra pestaña) a sumar un acceso, o a liberar una cuenta del cupo, vuelve
    // y encuentra la cuenta nueva o el lugar libre sin tener que cerrar y reabrir el modal.
    useEffect(() => {
        if (!isOpen || typeof window === 'undefined') return undefined;
        const onFocus = () => {
            if (saving) return;
            load();
            refetchAdAccountQuota();
        };
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [isOpen, load, saving]);

    const handleAccountChange = (accountId) => {
        setSaving(true);
        const body = {
            channel: channel,
            account_id: accountId,
            daily_limit_type: null,
            daily_limit_currency: null,
            daily_limit_value: null,
        };
        new APIClient(`${API_URL}client/${clientId}/ad_account/add`).call('POST', body).then((response) => {
            setSaving(false);
            if (!isOk(response)) {
                // Genérico a propósito: el `error_message` puede nombrar otra agencia («esa cuenta ya
                // está conectada a…») y eso no se muestra jamás (aislamiento cross-agency). La excepción
                // es el tope del plan, que con el genérico («intenta de nuevo») no se resuelve nunca (cc#230).
                // Con los números, el modal pasa al aviso del cupo lleno y lo dice ahí, sin un toast que lo
                // repita palabra por palabra al lado (QA-VIS-cc-230-03). Sin números, el toast y se relee el cupo.
                if (isPlanLimitError(response)) {
                    const fromError = quotaFromLinkError(response);
                    if (fromError) {
                        publishAdAccountQuota(fromError);
                    } else {
                        toast.error(planLimitText(t, null));
                        refetchAdAccountQuota();
                    }
                    return;
                }
                toast.error(t('control_central.link_account_save_error'));
                return;
            }
            refetchAdAccountQuota();
            if (onSuccess) onSuccess();
            toggle();
        });
    };

    if (!channel) return null;

    const info = CHANNEL_INFO_BY_CHANNEL[channel];
    const channelName = info ? info.Spanish : channel;
    // Una cuenta de CSV no se «conecta» en la agencia: se configura en las fuentes del cliente.
    const createsItsOwnAccounts = !!(info && info.CreateAccount);
    const connectPath = createsItsOwnAccounts ? `/client/${clientId}/conections` : '/agency/conections';
    const tOpts = {channel: channelName, interpolation: {escapeValue: false}};

    const offered = accounts ? orderAccounts(accounts, candidates, linkedAccountIds, clientId) : null;
    const describeAccount = (account) => {
        const keys = hintKeysOf(candidates[String(account.id)], clientId);
        return keys.length ? keys.map((key) => t(key)).join(' · ') : null;
    };

    const offersHelp = offersMissingAccountHelp(channel) && !createsItsOwnAccounts;
    // Lo buscado es una cuenta que el modal no ofrece porque ya es de este cliente: no falta nada.
    const isLinkedHere = (text) => {
        const hidden = (accounts || []).filter((account) => !(offered || []).includes(account));
        return accountsMatching(hidden, text).length > 0;
    };
    const handleSearchChange = (text, count) => {
        setQuery(text);
        // La cuenta está, vinculada y sin acceso: no hace falta la ayuda, hace falta reconectarla.
        const unreachableMatch = count === 0 ? findUnreachable(unreachable, text) : null;
        if (unreachableMatch) {
            recordUnreachableShown(unreachableSeen.current, channel, unreachableMatch);
            return;
        }
        // Lo que vino a buscar no está: es justo cuando la ayuda tiene que estar a la vista.
        if (offersHelp && text.trim() && count === 0 && !isLinkedHere(text)) setHelpOpen(true);
    };
    // Una búsqueda que no encuentra nada lo dice con lo que se buscó.
    const noResultsText = (text) => {
        const needle = (text || '').trim();
        const unreachableMatch = findUnreachable(unreachable, needle);
        if (unreachableMatch) {
            return <UnreachableLinkedNotice t={t} language={i18n && i18n.language} row={unreachableMatch}
                                            channel={channel} onReconnect={setReconnectRow}/>;
        }
        if (isLinkedHere(needle)) {
            return t('control_central.link_account_no_match_linked', {query: needle, interpolation: RAW});
        }
        return t('control_central.link_account_no_match', {...tOpts, query: needle});
    };
    const renderHelp = () => offersHelp && (
        <MissingAccountHelp channel={channel} channelName={channelName} query={query}
                            queryMatches={accountsMatching(offered || [], query).length > 0}
                            open={helpOpen} onToggle={() => setHelpOpen((open) => !open)} onDiscovered={load}/>
    );

    const renderBody = () => {
        if (loadFailed) {
            return (
                <div className="link-account-state">
                    <p>{t('control_central.link_account_load_error', tOpts)}</p>
                    <div className="link-account-state__actions">
                        <button type="button" className="btn -negative" onClick={load}>
                            {t('control_central.link_account_retry')}
                        </button>
                    </div>
                </div>
            );
        }
        if (accounts === null || saving) {
            return <div className="box-loader"><Spinner color="positive"/></div>;
        }
        // Cualquier alta nueva va a rebotar: antes que ofrecer cuentas (o explicar por qué no hay), el cupo.
        if (quotaIsFull(quota)) {
            return <AccountQuotaNotice quota={quota} surface={QUOTA_SURFACE}/>;
        }
        if (accounts.length === 0 && !createsItsOwnAccounts) {
            return <LinkAccountEmptyState channel={channel} channelName={channelName} reason={emptyReason} onRefresh={load}/>;
        }
        if (accounts.length === 0) {
            return (
                <div className="link-account-state">
                    <h4>{t('control_central.link_account_empty_title_create', tOpts)}</h4>
                    <p>{t('control_central.link_account_empty_text_create', tOpts)}</p>
                    <div className="link-account-state__actions">
                        <a className="btn -alt" href={connectPath} target="_blank" rel="noopener noreferrer">
                            {t('control_central.link_account_empty_connect_create', tOpts)}
                        </a>
                        <button type="button" className="btn -negative" onClick={load}>
                            {t('control_central.link_account_refresh')}
                        </button>
                    </div>
                </div>
            );
        }
        if (offered.length === 0) {
            return (
                <>
                    <div className="link-account-state">
                        <p>{t('control_central.link_account_all_linked', tOpts)}</p>
                    </div>
                    {renderHelp()}
                </>
            );
        }
        return (
            <>
                {/* Sin actividad (CSV, GA4, Search Console…) no hay orden por campañas activas que
                    prometer: la intro de siempre lo afirmaba igual (QA-cuentas-09). */}
                <p className="link-account-intro">{t(channelHasActivity(channel)
                    ? 'control_central.link_account_intro'
                    : 'control_central.link_account_intro_plain', tOpts)}</p>
                <div className="link-account-select">
                    <ChannelAccountSelect
                        allAccounts={offered}
                        selectedAccount={null}
                        onAccountChange={handleAccountChange}
                        channel={channel}
                        canCreateNewAccount={false}
                        describeAccount={describeAccount}
                        noResultsText={noResultsText}
                        onSearchChange={handleSearchChange}
                    />
                </div>
                {renderHelp()}
            </>
        );
    };

    // Sin cuentas, conectar ES el camino principal y ya está en el cuerpo: el pie no lo repite. En los
    // canales de pauta tampoco: lo reemplaza «¿No encuentras la cuenta?», que dice lo mismo y el porqué.
    const showNewAccessFooter = !loadFailed && accounts !== null && accounts.length > 0 && !createsItsOwnAccounts
        && !offersHelp && !quotaIsFull(quota);

    return (
        <Modal isOpen={isOpen} toggle={toggle}>
            {/* El aire entre el logo y el título lo da un `gap` (`.link-account-modal-header`): el
                espacio de texto que había se colapsaba en el `.modal-title`, que es flex (QA-VIS-cuentas-01). */}
            <ModalHeader toggle={toggle} className="link-account-modal-header">
                <ChannelLogo channel={channel} height={20} width={20} hspace={0} vspace={0} marginLeft={0} padding={0}/>
                {t('control_central.link_account_title', tOpts)}
            </ModalHeader>
            <ModalBody>
                <div className="modal-section">
                    {renderBody()}
                </div>
                {reconnectRow && (
                    <ReconnectAdAccountModal isOpen={true} onToggle={() => setReconnectRow(null)}
                                             adAccountData={reconnectDataOfLinked(reconnectRow)}
                                             onReconnected={() => setReconnectRow(null)}/>
                )}
            </ModalBody>
            {showNewAccessFooter && (
                <ModalFooter>
                    <div className="link-account-new-access">
                        {t('control_central.link_account_not_listed', tOpts)}{' '}
                        <a href={connectPath} target="_blank" rel="noopener noreferrer">
                            {t('control_central.link_account_add_access', tOpts)} &rarr;
                        </a>
                    </div>
                </ModalFooter>
            )}
        </Modal>
    );
}
